import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import BannerSlot from '../components/BannerSlot';

const REPORT_REASONS = [
  { v: 'spam', l: 'إعلان مكرر / سبام' },
  { v: 'fake', l: 'إعلان وهمي' },
  { v: 'inappropriate', l: 'محتوى غير لائق' },
  { v: 'fraud', l: 'احتيال / نصب' },
  { v: 'scam', l: 'محاولة نصب' },
  { v: 'offensive_language', l: 'ألفاظ مسيئة' },
  { v: 'harassment', l: 'تحرّش / إساءة' },
  { v: 'other', l: 'أخرى' },
];

interface AdDetail {
  id: number;
  title: string;
  description: string;
  price: number;
  price_type: string;
  quantity: number;
  weight: number | null;
  age_months: number | null;
  gender: string;
  status: string;
  contact_phone: string;
  show_phone: boolean;
  views_count: number;
  favorites_count: number;
  is_favorited?: boolean;
  is_promoted: boolean;
  is_featured: boolean;
  is_edited?: boolean;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  created_at: string;
  published_at: string;
  expires_at: string;
  category: { id: number; name_ar: string; slug: string } | null;
  subcategory: { id: number; name_ar: string } | null;
  age_category: { id: number; name_ar: string } | null;
  city: { id: number; name_ar: string } | null;
  delivery_available?: boolean;
  slaughter_available?: boolean;
  delivery_fee?: number | string | null;
  slaughter_fee?: number | string | null;
  user: {
    id: number;
    name: string;
    avatar: string | null;
    is_verified: boolean;
    account_type: string;
    rating_avg: string;
    city: string;
    created_at: string;
  } | null;
  images: { id: number; image_url: string; thumbnail_url: string | null; is_primary: boolean; sort_order: number }[];
  tags: { id: number; name_ar: string }[];
}

interface Comment {
  id: number;
  body: string;
  user: { id: number; name: string; avatar: string | null; is_verified: boolean } | null;
  is_owner: boolean;
  can_edit?: boolean;
  can_delete?: boolean;
  parent_id: number | null;
  created_at: string;
  replies: Comment[];
}

interface SimilarAd {
  id: number;
  title: string;
  price: number;
  city: { name_ar: string } | null;
  category: { name_ar: string } | null;
  images: { image_url: string }[];
}

const AdDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ad, setAd] = useState<AdDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [showPhone, setShowPhone] = useState(false);
  const [imageZoom, setImageZoom] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentsTotal, setCommentsTotal] = useState(0);
  const [commentBody, setCommentBody] = useState('');
  const [replyingId, setReplyingId] = useState<number | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [replySubmitting, setReplySubmitting] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentSubmitting, setCommentSubmitting] = useState(false);
  const [similarAds, setSimilarAds] = useState<SimilarAd[]>([]);
  const [favorited, setFavorited] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportCommentId, setReportCommentId] = useState<number | null>(null);
  const [rateOpen, setRateOpen] = useState(false);
  const [rateStars, setRateStars] = useState(0);
  const [rateComment, setRateComment] = useState('');
  const [rateSubmitting, setRateSubmitting] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        setLoading(true);
        const res = await api.get('/ads/' + id);
        setAd(res.data.data);
        setFavorited(!!res.data.data?.is_favorited);
        fetchSimilar(res.data.data?.category?.id);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchAd();
    fetchComments();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // جلب حالة المتابعة للبائع (إن كان المستخدم مسجّلاً)
  useEffect(() => {
    const sellerId = ad?.user?.id;
    if (!sellerId) return;
    if (!localStorage.getItem('user')) return;
    api.get('/users/' + sellerId + '/follow-status')
      .then((res) => setFollowing(!!res.data?.data?.is_following))
      .catch(() => {});
  }, [ad?.user?.id]);

  const fetchComments = async () => {
    try {
      setCommentLoading(true);
      const res = await api.get('/ads/' + id + '/comments');
      // الـ API يرجّع مجموعة مرقّمة: data.data = مصفوفة التعليقات، data.meta.total = الإجمالي
      const payload = res.data?.data;
      const list = Array.isArray(payload)
        ? payload
        : (payload?.data || payload?.comments || []);
      setComments(Array.isArray(list) ? list : []);
      setCommentsTotal(
        payload?.total ?? payload?.meta?.total ?? (Array.isArray(list) ? list.length : 0)
      );
    } catch (err) {
      console.error(err);
    } finally {
      setCommentLoading(false);
    }
  };

  const fetchSimilar = async (categoryId?: number) => {
    try {
      const params: any = { per_page: 7 };
      if (categoryId) params.category_id = categoryId;
      const res = await api.get('/ads', { params });
      const list: any[] = res.data?.data?.ads || [];
      setSimilarAds(list.filter((a: any) => String(a.id) !== String(id)).slice(0, 3));
    } catch (err) {
      console.error(err);
    }
  };

  const submitComment = async () => {
    if (!commentBody.trim()) return;
    try {
      setCommentSubmitting(true);
      const res = await api.post('/ads/' + id + '/comments', { body: commentBody });
      if (res.data.success) {
        setCommentBody('');
        fetchComments();
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        window.alert('سجّل دخولك أولاً');
      } else {
        window.alert(err.response?.data?.message || 'تعذّر إرسال التعليق');
      }
    } finally {
      setCommentSubmitting(false);
    }
  };

  const submitReply = async (parentId: number) => {
    if (!replyBody.trim()) return;
    try {
      setReplySubmitting(true);
      const res = await api.post('/ads/' + id + '/comments', { body: replyBody, parent_id: parentId });
      if (res.data.success) {
        setReplyBody('');
        setReplyingId(null);
        fetchComments();
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        window.alert('سجّل دخولك أولاً');
      } else {
        window.alert(err.response?.data?.message || 'تعذّر إرسال الرد');
      }
    } finally {
      setReplySubmitting(false);
    }
  };

  const deleteComment = async (commentId: number) => {
    const reason = window.prompt('سبب حذف التعليق (اختياري):', '');
    if (reason === null) return; // ألغى المستخدم
    try {
      await api.delete('/comments/' + commentId, { data: { reason } });
      fetchComments();
    } catch (err: any) {
      window.alert(err.response?.data?.message || 'تعذّر حذف التعليق');
    }
  };

  const toggleFavorite = async () => {
    try {
      if (favorited) {
        await api.delete('/favorites/' + id);
        setFavorited(false);
      } else {
        await api.post('/favorites/' + id);
        setFavorited(true);
      }
    } catch (err: any) {
      if (err.response?.status === 401) window.alert('سجّل دخولك أولاً');
      else window.alert(err.response?.data?.message || 'تعذّر تحديث المفضلة');
    }
  };

  const closeReport = () => { setReportOpen(false); setReportCommentId(null); setReportReason(''); setReportDetails(''); };

  const submitReport = async () => {
    if (!reportReason) { window.alert('اختر سبب البلاغ'); return; }
    const isComment = reportCommentId !== null;
    try {
      setReportSubmitting(true);
      await api.post('/reports', {
        report_type: isComment ? 'comment' : 'ad',
        reportable_id: isComment ? reportCommentId : id,
        reason: reportReason,
        details: reportDetails,
      });
      window.alert('تم إرسال البلاغ، شكراً لك');
      closeReport();
    } catch (err: any) {
      if (err.response?.status === 401) window.alert('سجّل دخولك أولاً');
      else window.alert(err.response?.data?.message || 'تعذّر إرسال البلاغ');
    } finally {
      setReportSubmitting(false);
    }
  };

  const submitReview = async () => {
    if (!ad?.user) return;
    if (rateStars < 1) { window.alert('اختر عدد النجوم'); return; }
    const hasToken = Boolean(localStorage.getItem('user'));
    if (!hasToken) { window.alert('سجّل دخولك أولاً لتقييم البائع'); navigate('/login'); return; }
    try {
      setRateSubmitting(true);
      await api.post('/reviews', {
        reviewed_id: ad.user.id,
        ad_id: ad.id,
        rating: rateStars,
        comment: rateComment || null,
        role: 'buyer',
      });
      window.alert('تم إرسال تقييمك للبائع، شكراً لك');
      setRateOpen(false); setRateStars(0); setRateComment('');
      // إعادة تحميل الإعلان لتحديث متوسط تقييم البائع
      try { const res = await api.get('/ads/' + id); setAd(res.data.data); } catch { /* تجاهل */ }
    } catch (err: any) {
      if (err.response?.status === 401) { window.alert('سجّل دخولك أولاً'); navigate('/login'); }
      else window.alert(err.response?.data?.message || 'تعذّر إرسال التقييم');
    } finally {
      setRateSubmitting(false);
    }
  };

  const toggleFollow = async () => {
    if (!ad?.user) return;
    if (!localStorage.getItem('user')) { window.alert('سجّل دخولك أولاً للمتابعة'); navigate('/login'); return; }
    try {
      setFollowLoading(true);
      if (following) {
        await api.delete('/users/' + ad.user.id + '/follow');
        setFollowing(false);
      } else {
        await api.post('/users/' + ad.user.id + '/follow');
        setFollowing(true);
      }
    } catch (err: any) {
      if (err.response?.status === 401) { window.alert('سجّل دخولك أولاً'); navigate('/login'); }
      else window.alert(err.response?.data?.message || 'تعذّر تنفيذ المتابعة');
    } finally {
      setFollowLoading(false);
    }
  };

  const startConversation = async () => {
    if (!ad?.user) return;
    api.post('/ads/' + ad.id + '/contact', { type: 'message' }).catch(() => {});
    const hasToken = Boolean(localStorage.getItem('user'));
    if (!hasToken) { window.alert('سجّل دخولك أولاً لبدء محادثة'); navigate('/login'); return; }
    try {
      const res = await api.post('/conversations', { seller_id: ad.user.id, ad_id: ad.id });
      const convId = res.data?.data?.id;
      navigate(convId ? '/my/conversations/' + convId : '/my/conversations');
    } catch (err: any) {
      if (err.response?.status === 401) { window.alert('سجّل دخولك أولاً'); navigate('/login'); }
      else window.alert(err.response?.data?.message || 'تعذّر بدء المحادثة');
    }
  };

  const timeAgo = (dateStr: string) => {
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return 'الآن';
    if (diff < 3600) return 'قبل ' + Math.floor(diff / 60) + ' دقيقة';
    if (diff < 86400) return 'قبل ' + Math.floor(diff / 3600) + ' ساعة';
    if (diff < 2592000) return 'قبل ' + Math.floor(diff / 86400) + ' يوم';
    return 'قبل ' + Math.floor(diff / 2592000) + ' شهر';
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 900, margin: '25px auto', padding: '0 20px', fontFamily: "'Cairo', sans-serif" }}>
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #E0E0E0', padding: 40 }}>
          <div style={{ height: 20, background: '#F5F5F5', borderRadius: 6, width: '40%', marginBottom: 15 }}></div>
          <div style={{ height: 30, background: '#F5F5F5', borderRadius: 6, width: '70%', marginBottom: 15 }}></div>
          <div style={{ height: 200, background: '#F5F5F5', borderRadius: 12 }}></div>
        </div>
      </div>
    );
  }

  if (!ad) {
    return (
      <div style={{ maxWidth: 900, margin: '60px auto', textAlign: 'center', fontFamily: "'Cairo', sans-serif" }}>
        <p style={{ color: '#9E9E9E', fontSize: 18, marginBottom: 10 }}>الإعلان غير موجود</p>
        <Link to="/ads" style={{ color: '#005343', fontSize: 14 }}>العودة للإعلانات</Link>
      </div>
    );
  }

  const currentUserId = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}')?.id; } catch { return undefined; } })();
  const sellerRating = ad.user ? Number(ad.user.rating_avg) || 0 : 0;
  const priceMap: Record<string, string> = { fixed: 'ثابت', negotiable: 'قابل للتفاوض', auction: 'مزاد' };
  const priceTypeLabel = priceMap[ad.price_type] || '';

  const specs = [
    { label: 'التصنيف', value: ad.category?.name_ar, icon: '⭐' },
    { label: 'السلالة', value: ad.subcategory?.name_ar, icon: '🐑' },
    { label: 'النوع', value: ad.age_category?.name_ar, icon: '📅' },
    { label: 'الكمية', value: ad.quantity ? ad.quantity + ' رأس' : null, icon: '📊' },
    { label: 'الوزن', value: ad.weight ? ad.weight + ' كجم' : null, icon: '⚖️' },
    { label: 'العمر', value: ad.age_months ? ad.age_months + ' شهر' : null, icon: '⏰' },
    { label: 'الموقع', value: ad.city?.name_ar, icon: '📍' },
    { label: 'التوصيل', value: ad.delivery_available ? (ad.delivery_fee ? Number(ad.delivery_fee).toLocaleString() + ' ر.س' : 'مجاني') : null, icon: '🚚' },
    { label: 'الذبح', value: ad.slaughter_available ? (ad.slaughter_fee ? Number(ad.slaughter_fee).toLocaleString() + ' ر.س' : 'مجاني') : null, icon: '🔪' },
  ].filter(s => s.value);

  const renderComment = (comment: Comment, isReply?: boolean) => {
    const avatarColors = ['#005343','#F44336','#4CAF50','#7B1FA2','#FF9800','#00BCD4','#E91E63','#424242'];
    const colorIdx = (comment.user?.id || 0) % avatarColors.length;
    const avatarBg = comment.is_owner ? '#005343' : avatarColors[colorIdx];

    if (isReply) {
      return (
        <div key={comment.id} style={{ marginRight: 52, padding: '15px 20px', marginTop: 12, background: '#FAFAFA', borderRadius: 10, borderRight: '3px solid #BFDEDA' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, overflow: 'hidden' }}>
                {comment.user?.avatar ? <img src={comment.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (comment.user?.name?.charAt(0) || '؟')}
              </div>
              {comment.user?.is_verified && <div style={{ position: 'absolute', bottom: -1, left: -1, width: 14, height: 14, background: '#3B82F6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, color: '#fff', border: '2px solid #fff' }}>✓</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>{comment.user?.name || 'مستخدم'}</span>
                {comment.is_owner && <span style={{ background: '#E6F2F0', color: '#005343', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>صاحب الإعلان</span>}
                {comment.user?.is_verified && <span style={{ fontSize: 10, fontWeight: 600, color: '#3B82F6' }}>✓ موثّق</span>}
                <span style={{ fontSize: 12, color: '#BDBDBD' }}>{timeAgo(comment.created_at)}</span>
              </div>
            </div>
          </div>
          <div style={{ paddingRight: 44, fontSize: 13, color: '#424242', lineHeight: 1.7, marginBottom: 8 }}>{comment.body}</div>
          <div style={{ paddingRight: 44, display: 'flex', gap: 15 }}>
            {!comment.can_edit && <button onClick={() => { setReportCommentId(comment.id); setReportReason(''); setReportDetails(''); }} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>🚩 بلاغ</button>}
            {comment.can_delete && <button onClick={() => deleteComment(comment.id)} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>حذف</button>}
          </div>
        </div>
      );
    }

    return (
      <div key={comment.id} style={{ padding: '20px 30px', borderBottom: '1px solid #F5F5F5' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: avatarBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 16, overflow: 'hidden' }}>
              {comment.user?.avatar ? <img src={comment.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (comment.user?.name?.charAt(0) || '؟')}
            </div>
            {comment.user?.is_verified && <div style={{ position: 'absolute', bottom: -1, left: -1, width: 16, height: 16, background: '#3B82F6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: '#fff', border: '2px solid #fff' }}>✓</div>}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' as const }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#212121' }}>{comment.user?.name || 'مستخدم'}</span>
              {comment.is_owner && <span style={{ background: '#E6F2F0', color: '#005343', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 8 }}>صاحب الإعلان</span>}
              {comment.user?.is_verified && <span style={{ fontSize: 10, fontWeight: 600, color: '#3B82F6' }}>✓ موثّق</span>}
              <span style={{ fontSize: 12, color: '#BDBDBD' }}>{timeAgo(comment.created_at)}</span>
            </div>
          </div>
        </div>
        <div style={{ paddingRight: 52, fontSize: 14, color: '#424242', lineHeight: 1.7, marginBottom: 10 }}>{comment.body}</div>
        <div style={{ paddingRight: 52, display: 'flex', gap: 15 }}>
          <button onClick={() => { setReplyingId(replyingId === comment.id ? null : comment.id); setReplyBody(''); }} style={{ background: 'none', border: 'none', color: replyingId === comment.id ? '#005343' : '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>↩ رد</button>
          {!comment.can_edit && <button onClick={() => { setReportCommentId(comment.id); setReportReason(''); setReportDetails(''); }} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>🚩 بلاغ</button>}
          {comment.can_delete && <button onClick={() => deleteComment(comment.id)} style={{ background: 'none', border: 'none', color: '#9E9E9E', fontSize: 12, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", fontWeight: 600 }}>حذف</button>}
        </div>

        {replyingId === comment.id && (
          <div style={{ paddingRight: 52, marginTop: 10, display: 'flex', gap: 8 }}>
            <input
              type="text"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitReply(comment.id); }}
              placeholder={'رد على ' + (comment.user?.name || '') + '...'}
              maxLength={500}
              autoFocus
              style={{ flex: 1, border: '1px solid #E0E0E0', borderRadius: 8, padding: '9px 12px', fontSize: 13, fontFamily: "'Cairo', sans-serif", outline: 'none' }}
            />
            <button
              onClick={() => submitReply(comment.id)}
              disabled={replySubmitting || !replyBody.trim()}
              style={{ background: '#005343', color: '#fff', border: 'none', borderRadius: 8, padding: '0 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Cairo', sans-serif", opacity: (replySubmitting || !replyBody.trim()) ? 0.5 : 1, flexShrink: 0 }}
            >
              {replySubmitting ? '...' : 'إرسال'}
            </button>
          </div>
        )}

        {comment.replies && comment.replies.length > 0 && comment.replies.map(r => renderComment(r, true))}
      </div>
    );
  };

  return (
    <>
      <style>{`
        .adp-wrap{max-width:900px;margin:25px auto;padding:0 20px;font-family:'Cairo',sans-serif}
        .adp-card{background:#fff;border-radius:20px;border:1px solid #E6ECE9;overflow:hidden;margin-bottom:20px}
        .adp-header{background:#fff;padding:24px 30px;border-bottom:1px solid #EEEEEE}
        .adp-header-top{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:12px}
        .adp-title{font-size:26px;font-weight:800;color:#17241F;margin:0;line-height:1.4}
        .adp-feat-badge{display:inline-flex;align-items:center;gap:4px;background:#B8893C;color:#fff;font-size:12px;font-weight:800;padding:4px 12px;border-radius:9999px;margin-right:10px;vertical-align:middle}
        .adp-btn-report{display:flex;align-items:center;gap:5px;background:none;border:1px solid #BDBDBD;padding:6px 14px;border-radius:20px;font-family:'Cairo',sans-serif;font-size:11px;color:#9E9E9E;cursor:pointer;white-space:nowrap}
        .adp-btn-report:hover{border-color:#F44336;color:#F44336;background:rgba(244,67,54,0.05)}
        .adp-meta{display:flex;align-items:center;gap:20px;font-size:13px;color:#757575;flex-wrap:wrap}
        .adp-meta span{display:flex;align-items:center;gap:5px}
        .adp-meta-icon{color:#005343}
        .adp-seller{display:flex;align-items:center;justify-content:space-between;padding:18px 30px;border-bottom:1px solid #EEEEEE}
        .adp-seller-right{display:flex;align-items:center;gap:12px;text-decoration:none;color:inherit}
        .adp-avatar-wrap{position:relative;flex-shrink:0}
        .adp-avatar{width:48px;height:48px;border-radius:50%;background:#005343;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:700;color:#fff;overflow:hidden}
        .adp-v-badge{position:absolute;bottom:-2px;left:-2px;width:20px;height:20px;background:#3B82F6;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:11px;color:#fff;border:2px solid #fff}
        .adp-seller-name{font-size:15px;font-weight:700;color:#212121}
        .adp-v-text{display:inline-flex;align-items:center;gap:3px;background:#EFF6FF;color:#3B82F6;font-size:10px;font-weight:700;padding:2px 8px;border-radius:8px}
        .adp-seller-area{font-size:12px;color:#9E9E9E;margin-top:2px}
        .adp-seller-since{font-size:11px;color:#BDBDBD;margin-top:2px}
        .adp-header-mid{display:flex;justify-content:space-between;align-items:center;gap:16px;flex-wrap:wrap;margin-top:12px}
        .adp-hseller{display:flex;align-items:center;gap:12px;flex-wrap:wrap}
        .adp-hseller-info{display:flex;align-items:center;gap:10px;text-decoration:none;color:inherit}
        .adp-avatar-sm{width:38px;height:38px;font-size:16px}
        .adp-hseller-text{display:flex;flex-direction:column;gap:2px}
        .adp-hseller-name{font-size:14px;font-weight:700;color:#212121;display:flex;align-items:center;gap:6px;flex-wrap:wrap}
        .adp-hseller-sub{font-size:12px;color:#9E9E9E;display:flex;align-items:center;gap:5px}
        .adp-store-badge{background:#E6F2F0;color:#005343;font-size:10px;padding:2px 8px;border-radius:8px;font-weight:700}
        .adp-hseller-actions{display:flex;gap:8px;flex-shrink:0}
        .adp-btn-sm{padding:6px 16px;font-size:12px}
        .adp-btn-rate{background:#fff;border:1.5px solid #F59E0B;color:#B45309;border-radius:20px;padding:6px 14px;font-size:12px;font-weight:700;cursor:pointer;font-family:'Cairo',sans-serif;white-space:nowrap}
        .adp-btn-follow{background:#005343;color:#fff;border:none;padding:8px 22px;border-radius:20px;font-family:'Cairo',sans-serif;font-size:13px;font-weight:600;cursor:pointer}
        .adp-btn-follow:hover{background:#003A2F}
        .adp-desc{padding:20px 30px 10px;font-size:15px;color:#424242;line-height:1.8;white-space:pre-wrap}
        .adp-specs-inline{padding:2px 30px 18px;display:flex;flex-wrap:wrap;gap:6px 18px;align-items:center;border-bottom:1px solid #EEEEEE}
        .adp-specs-inline .sp{font-size:14px;color:#212121;white-space:nowrap}
        .adp-specs-inline .sp .lbl{color:#9E9E9E;font-size:12px;margin-left:3px}
        .adp-specs-inline .sp b{font-weight:600;color:#005343}
        .adp-price{padding:20px 30px;display:flex;align-items:center;gap:15px;border-bottom:1px solid #EEEEEE}
        .adp-price-label{font-size:14px;color:#9E9E9E}
        .adp-price-val{font-size:26px;font-weight:800;color:#005343;font-family:'Cairo',sans-serif}
        .adp-price-val small{font-size:14px;font-weight:500;color:#5D6B65;font-family:'Tajawal','Cairo',sans-serif}
        .adp-price-type{font-size:12px;color:#9E9E9E;margin-right:10px}
        .adp-specs{display:grid;grid-template-columns:repeat(3,1fr)}
        .adp-spec{padding:16px 12px;text-align:center;border-bottom:1px solid #EEEEEE;border-left:1px solid #EEEEEE}
        .adp-spec:hover{background:#FAFAFA}
        .adp-spec-icon{font-size:18px;margin-bottom:8px}
        .adp-spec-label{font-size:10px;color:#BDBDBD;margin-bottom:4px}
        .adp-spec-value{font-size:13px;font-weight:500;color:#212121}
        .adp-imgs{padding:20px 30px;border-top:1px solid #EEEEEE}
        .adp-img-main{width:100%;height:380px;object-fit:contain;border-radius:16px;cursor:pointer;background:#F4F6F3;border:1px solid #EAEFEC}
        .adp-img-thumbs{display:flex;gap:8px;margin-top:12px;overflow-x:auto;padding-bottom:4px}
        .adp-img-thumb{width:70px;height:70px;border-radius:8px;object-fit:cover;cursor:pointer;border:2px solid #E0E0E0}
        .adp-img-thumb-active{border-color:#005343}
        .adp-img-empty{width:100%;height:300px;background:linear-gradient(135deg,#E6F2F0,#BFDEDA);border-radius:12px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;color:#005343;border:2px dashed #BFDEDA}
        .adp-btns{padding:15px 30px 0;display:flex;gap:8px;flex-wrap:wrap}
        .adp-btns-row{display:contents}
        .adp-btn{padding:8px 12px;border:none;border-radius:8px;font-size:12px;font-weight:500;flex:1;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;font-family:'Cairo',sans-serif;transition:all 0.2s;text-decoration:none}
        .adp-btn-call{background:#005343;color:#fff}.adp-btn-call:hover{background:#003A2F}
        .adp-btn-wa{background:#25A55F;color:#fff}.adp-btn-wa:hover{background:#1F8E51}
        .adp-btn-chat{background:#fff;color:#005343;border:1.5px solid #005343}.adp-btn-chat:hover{background:#E6F2F0}
        .adp-tags{padding:0 30px 10px;display:flex;flex-wrap:wrap;gap:8px}
        .adp-tag-hash{color:#005343;font-size:12px;font-weight:700;background:#E6F2F0;padding:3px 11px;border-radius:9999px}
        .adp-btn-sec{background:#fff;color:#616161;border:1px solid #E0E0E0}.adp-btn-sec:hover{background:#FAFAFA}
        .adp-warn{padding:0 30px 25px;margin-top:20px}
        .adp-warn-box{background:#FDF6E3;border:1px solid #F0E2BF;border-radius:12px;padding:12px 16px;display:flex;align-items:center;gap:10px;font-size:13.5px;color:#7A5521;line-height:1.8}
        .adp-comments{background:#fff;border-radius:20px;border:1px solid #E6ECE9;overflow:hidden;margin-bottom:20px}
        .adp-comments-hdr{padding:20px 30px;border-bottom:1px solid #EEEEEE;display:flex;align-items:center;gap:10px}
        .adp-comments-hdr h3{font-size:18px;font-weight:700;color:#212121;margin:0}
        .adp-comments-count{background:#005343;color:#fff;font-size:12px;font-weight:700;padding:2px 10px;border-radius:12px}
        .adp-comment-input{padding:20px 30px;display:flex;gap:12px;align-items:flex-start;border-bottom:1px solid #EEEEEE;flex-direction:column}
        .adp-comment-input-row{display:flex;gap:12px;width:100%}
        .adp-input{flex:1;padding:12px 16px;border:1.5px solid #E0E0E0;border-radius:10px;font-family:'Cairo',sans-serif;font-size:14px;outline:none;background:#FAFAFA}
        .adp-input:focus{border-color:#005343;background:#fff}
        .adp-btn-send{background:#005343;color:#fff;border:none;padding:12px 24px;border-radius:10px;font-family:'Cairo',sans-serif;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap}
        .adp-btn-send:hover{background:#003A2F}.adp-btn-send:disabled{background:#BDBDBD}
        .adp-reply-bar{display:flex;align-items:center;gap:8px;background:#E6F2F0;padding:8px 14px;border-radius:8px;font-size:12px;color:#005343;width:100%;margin-bottom:8px}
        .adp-reply-bar button{background:none;border:none;color:#9E9E9E;cursor:pointer;margin-right:auto;font-size:14px}
        .adp-no-comments{padding:40px 30px;text-align:center;color:#BDBDBD;font-size:14px}
        .adp-similar-hdr{display:flex;align-items:center;justify-content:space-between;margin-bottom:15px}
        .adp-similar-hdr h3{font-size:18px;font-weight:700;color:#212121;margin:0}
        .adp-similar-hdr a{font-size:13px;color:#005343;text-decoration:none;font-weight:600}
        .adp-similar-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:15px;margin-bottom:20px}
        .adp-similar-card{background:#fff;border-radius:18px;border:1px solid #E6ECE9;overflow:hidden;text-decoration:none;color:inherit;transition:all 0.2s}
        .adp-similar-card:hover{box-shadow:0 4px 15px rgba(0,0,0,0.08);transform:translateY(-2px)}
        .adp-similar-img{width:100%;height:140px;background:linear-gradient(135deg,#E6F2F0,#BFDEDA);display:flex;align-items:center;justify-content:center;overflow:hidden}
        .adp-similar-img img{width:100%;height:100%;object-fit:cover}
        .adp-similar-body{padding:14px 16px}
        .adp-similar-title{font-size:14px;font-weight:700;color:#212121;margin-bottom:6px}
        .adp-similar-meta{display:flex;align-items:center;justify-content:space-between}
        .adp-similar-price{font-size:16px;font-weight:800;color:#005343;font-family:'Inter','Cairo',sans-serif}
        .adp-similar-price small{font-size:11px;font-weight:500;font-family:'Cairo',sans-serif}
        .adp-similar-loc{font-size:11px;color:#9E9E9E}
        .adp-breadcrumb{font-size:13px;display:flex;align-items:center;gap:5px;flex-wrap:wrap;margin-bottom:15px;padding:5px 0}
        .adp-breadcrumb a{color:#005343;text-decoration:none}
        .adp-breadcrumb .sep{color:#BDBDBD;margin:0 2px}
        .adp-lightbox{position:fixed;inset:0;z-index:3000;background:rgba(0,0,0,0.9);display:flex;align-items:center;justify-content:center}
        .adp-lightbox-close{position:absolute;top:15px;left:15px;color:rgba(255,255,255,0.7);font-size:30px;cursor:pointer;background:none;border:none;z-index:10}
        .adp-lightbox-img{max-width:90vw;max-height:85vh;object-fit:contain}
        .adp-lightbox-nav{position:absolute;top:50%;transform:translateY(-50%);color:rgba(255,255,255,0.5);font-size:40px;cursor:pointer;background:none;border:none;z-index:10}
        .adp-lightbox-nav:hover{color:#fff}
        @media(max-width:600px){.adp-specs{grid-template-columns:repeat(2,1fr)}.adp-title{font-size:20px}.adp-price-val{font-size:22px}.adp-similar-grid{grid-template-columns:1fr}.adp-header,.adp-seller,.adp-desc,.adp-price,.adp-imgs,.adp-btns,.adp-warn,.adp-comments-hdr,.adp-comment-input{padding-left:15px;padding-right:15px}}
      `}</style>

      <div className="adp-wrap">
        <BannerSlot position="ad_detail" />
        <div className="adp-breadcrumb">
          <Link to="/">الرئيسية</Link>
          <span className="sep">/</span>
          <Link to="/ads">الإعلانات</Link>
          {ad.category && (<><span className="sep">/</span><Link to={'/categories/' + ad.category.slug}>{ad.category.name_ar}</Link></>)}
          {ad.subcategory && (<><span className="sep">/</span><span style={{ color: '#757575' }}>{ad.subcategory.name_ar}</span></>)}
        </div>

        <div className="adp-card">
          <div className="adp-header">
            <div className="adp-header-top">
              <h1 className="adp-title">{ad.title}{ad.is_featured && <span className="adp-feat-badge">✨ مميز</span>}</h1>
              <button className="adp-btn-report" onClick={() => setReportOpen(true)}>🚩 إبلاغ عن الإعلان</button>
            </div>
            <div className="adp-header-mid">
              <div className="adp-meta">
              {ad.city && <span><span className="adp-meta-icon">📍</span> {ad.city.name_ar}</span>}
              <span><span className="adp-meta-icon">🕐</span> {timeAgo(ad.published_at || ad.created_at)}</span>
              <span><span className="adp-meta-icon">👁</span> {ad.views_count || 0} مشاهدة</span>
            </div>
            {ad.user && (
              <div className="adp-hseller">
                <Link to={'/users/' + ad.user.id} className="adp-hseller-info">
                  <div className="adp-avatar-wrap">
                    <div className="adp-avatar adp-avatar-sm">
                      {ad.user.avatar ? <img src={ad.user.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : ad.user.name.charAt(0)}
                    </div>
                    {ad.user.is_verified && <div className="adp-v-badge">✓</div>}
                  </div>
                  <div className="adp-hseller-text">
                    <span className="adp-hseller-name">
                      {ad.user.name}
                      {ad.user.is_verified && <span className="adp-v-text">✓ موثّق</span>}
                      {ad.user.account_type === 'company' && <span className="adp-store-badge">متجر</span>}
                    </span>
                    <span className="adp-hseller-sub">
                      {sellerRating > 0 && <span style={{ color: '#F59E0B', fontWeight: 700 }}>★ {sellerRating.toFixed(1)}</span>}
                      {ad.user.city && <span>{sellerRating > 0 ? '· ' : ''}{ad.user.city}</span>}
                    </span>
                  </div>
                </Link>
                {currentUserId !== ad.user.id && (
                  <div className="adp-hseller-actions">
                    <button className="adp-btn-follow adp-btn-sm" onClick={toggleFollow} disabled={followLoading} style={following ? { background: '#E6F2F0', color: '#005343' } : undefined}>{following ? '✓ متابَع' : '+ متابعة'}</button>
                    <button className="adp-btn-rate" onClick={() => { setRateStars(0); setRateComment(''); setRateOpen(true); }}>⭐ قيّم</button>
                  </div>
                )}
              </div>
            )}
            </div>
          </div>

          <div className="adp-desc">{ad.description}</div>

          {ad.tags && ad.tags.length > 0 && (
            <div className="adp-tags">
              {ad.tags.map(t => <span key={t.id} className="adp-tag-hash">#{t.name_ar}</span>)}
            </div>
          )}

          {specs.length > 0 && (
            <div className="adp-specs-inline">
              {specs.map((spec, i) => (
                <span key={i} className="sp"><span className="lbl">{spec.label}</span> <b>{spec.value}</b></span>
              ))}
            </div>
          )}

          {ad.price && (
            <div className="adp-price">
              <span className="adp-price-label">السعر</span>
              <span className="adp-price-val">{Number(ad.price).toLocaleString()} <small>ر.س</small></span>
              {priceTypeLabel && <span className="adp-price-type">({priceTypeLabel})</span>}
            </div>
          )}

          <div className="adp-imgs">
            {ad.images && ad.images.length > 0 ? (
              <>
                <img src={ad.images[activeImage]?.image_url} alt={ad.title} className="adp-img-main" onClick={() => setImageZoom(true)} />
                {ad.images.length > 1 && (
                  <div className="adp-img-thumbs">
                    {ad.images.map((img, idx) => (
                      <img key={img.id} src={img.thumbnail_url || img.image_url} alt="" className={'adp-img-thumb' + (activeImage === idx ? ' adp-img-thumb-active' : '')} onClick={() => setActiveImage(idx)} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="adp-img-empty">
                <span style={{ fontSize: 50 }}>📷</span>
                <span style={{ fontSize: 14, fontWeight: 600 }}>صورة الإعلان</span>
              </div>
            )}
          </div>

          <div className="adp-btns">
            <div className="adp-btns-row">
              {ad.contact_phone && (
                <button className="adp-btn adp-btn-call" onClick={() => { if (showPhone) { api.post('/ads/' + ad.id + '/contact', { type: 'call' }).catch(() => {}); window.location.href = 'tel:' + ad.contact_phone; } else { setShowPhone(true); } }}>
                  📞 {showPhone ? ad.contact_phone : 'اتصال'}
                </button>
              )}
              {ad.contact_phone && (
                <a className="adp-btn adp-btn-wa" href={'https://wa.me/966' + ad.contact_phone.replace(/^0/, '')} target="_blank" rel="noopener noreferrer" onClick={() => api.post('/ads/' + ad.id + '/contact', { type: 'whatsapp' }).catch(() => {})}>
                  💚 واتساب
                </a>
              )}
              {ad.user && (
                <button type="button" className="adp-btn adp-btn-chat" onClick={startConversation}>
                  💬 محادثة
                </button>
              )}
            </div>
            {(ad as any).has_video && /^https?:\/\//i.test((ad as any).video_url || '') && (
              <a className="adp-btn" href={(ad as any).video_url} target="_blank" rel="noopener noreferrer"
                 style={{ background: '#F5A623', color: '#fff', marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                ▶ شاهد الفيديو
              </a>
            )}
            <div className="adp-btns-row">
              <button className="adp-btn adp-btn-sec" onClick={toggleFavorite}><span style={{ color: '#F44336' }}>{favorited ? '♥' : '♡'}</span> {favorited ? 'في المفضلة' : 'مفضلة'}</button>
              <button className="adp-btn adp-btn-sec" onClick={() => { if (navigator.share) { navigator.share({ title: ad.title, url: window.location.href }); } else { navigator.clipboard.writeText(window.location.href); } }}>
                <span style={{ color: '#005343' }}>🔗</span> مشاركة
              </button>
            </div>
          </div>

          <div className="adp-warn">
            <div className="adp-warn-box">
              <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
              <span>تأكد من معاينة المواشي قبل الشراء. منصة الجلب غير مسؤولة عن أي صفقات تتم بين البائع والمشتري.</span>
            </div>
          </div>

          {ad.is_edited && (
            <div style={{ textAlign: 'center', padding: '10px 0 2px', fontSize: 12, color: '#9E9E9E' }}>
              ✎ تم التعديل
            </div>
          )}
        </div>

        <div className="adp-comments">
          <div className="adp-comments-hdr">
            <h3>التعليقات والردود</h3>
            {commentsTotal > 0 && <span className="adp-comments-count">{commentsTotal}</span>}
          </div>
          <div className="adp-comment-input">
            <div className="adp-comment-input-row">
              <input type="text" className="adp-input" value={commentBody} onChange={(e) => setCommentBody(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submitComment()} placeholder="اكتب تعليق أو سوم..." maxLength={500} />
              <button className="adp-btn-send" onClick={submitComment} disabled={commentSubmitting || !commentBody.trim()}>{commentSubmitting ? '...' : 'إرسال'}</button>
            </div>
          </div>
          <div>
            {commentLoading ? (
              <div style={{ padding: '40px 30px', textAlign: 'center', color: '#BDBDBD' }}>جاري التحميل...</div>
            ) : comments.length > 0 ? (
              comments.map(c => renderComment(c))
            ) : (
              <div className="adp-no-comments">لا توجد تعليقات بعد</div>
            )}
          </div>
        </div>

        {similarAds.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div className="adp-similar-hdr">
              <h3>إعلانات مشابهة</h3>
              <Link to="/ads">عرض الكل ←</Link>
            </div>
            <div className="adp-similar-grid">
              {similarAds.slice(0, 3).map(sa => (
                <Link key={sa.id} to={'/ads/' + sa.id} className="adp-similar-card">
                  <div className="adp-similar-img">
                    {sa.images && sa.images.length > 0 ? <img src={sa.images[0].image_url} alt="" /> : <span style={{ fontSize: 40, color: '#005343' }}>🐑</span>}
                  </div>
                  <div className="adp-similar-body">
                    <div className="adp-similar-title">{sa.title}</div>
                    <div className="adp-similar-meta">
                      <span className="adp-similar-price">{Number(sa.price).toLocaleString()} <small>ر.س</small></span>
                      {sa.city && <span className="adp-similar-loc">📍 {sa.city.name_ar}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {imageZoom && ad.images.length > 0 && (
        <div className="adp-lightbox" onClick={() => setImageZoom(false)}>
          <button className="adp-lightbox-close" onClick={() => setImageZoom(false)}>✕</button>
          {ad.images.length > 1 && (
            <>
              <button className="adp-lightbox-nav" style={{ right: 15 }} onClick={(e) => { e.stopPropagation(); setActiveImage(prev => (prev + 1) % ad.images.length); }}>‹</button>
              <button className="adp-lightbox-nav" style={{ left: 15 }} onClick={(e) => { e.stopPropagation(); setActiveImage(prev => (prev - 1 + ad.images.length) % ad.images.length); }}>›</button>
            </>
          )}
          <img src={ad.images[activeImage]?.image_url} alt={ad.title} className="adp-lightbox-img" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {(reportOpen || reportCommentId !== null) && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={closeReport}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 440, padding: 22, fontFamily: "'Cairo', sans-serif" }} dir="rtl" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#212121' }}>{reportCommentId !== null ? '🚩 إبلاغ عن تعليق' : '🚩 إبلاغ عن الإعلان'}</h3>
              <button onClick={closeReport} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9E9E9E', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <label style={{ display: 'block', fontSize: 13, color: '#616161', marginBottom: 6, fontWeight: 600 }}>سبب البلاغ *</label>
            <select value={reportReason} onChange={(e) => setReportReason(e.target.value)} style={{ width: '100%', padding: '11px 12px', border: '1.5px solid #E0E0E0', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, marginBottom: 14, background: '#fff' }}>
              <option value="">اختر السبب</option>
              {REPORT_REASONS.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
            <label style={{ display: 'block', fontSize: 13, color: '#616161', marginBottom: 6, fontWeight: 600 }}>تفاصيل إضافية (اختياري)</label>
            <textarea value={reportDetails} onChange={(e) => setReportDetails(e.target.value)} rows={3} maxLength={1000} placeholder="اشرح المشكلة..." style={{ width: '100%', padding: '11px 12px', border: '1.5px solid #E0E0E0', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, resize: 'none', marginBottom: 16, outline: 'none' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={submitReport} disabled={reportSubmitting} style={{ flex: 1, background: '#F44336', color: '#fff', border: 'none', padding: '11px', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: reportSubmitting ? 0.6 : 1 }}>{reportSubmitting ? 'جاري الإرسال...' : 'إرسال البلاغ'}</button>
              <button onClick={closeReport} style={{ padding: '11px 20px', background: '#F5F5F5', color: '#616161', border: 'none', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>إلغاء</button>
            </div>
          </div>
        </div>
      )}

      {rateOpen && ad.user && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 3000, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setRateOpen(false)}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420, padding: 22, fontFamily: "'Cairo', sans-serif" }} dir="rtl" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#212121' }}>⭐ تقييم البائع</h3>
              <button onClick={() => setRateOpen(false)} style={{ background: 'none', border: 'none', fontSize: 24, color: '#9E9E9E', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <p style={{ fontSize: 13, color: '#757575', margin: '0 0 14px' }}>كيف كانت تجربتك مع {ad.user.name}؟</p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
              {[1, 2, 3, 4, 5].map((s) => (
                <button key={s} onClick={() => setRateStars(s)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 34, lineHeight: 1, color: s <= rateStars ? '#F59E0B' : '#E0E0E0', padding: 0 }}>★</button>
              ))}
            </div>
            <label style={{ display: 'block', fontSize: 13, color: '#616161', marginBottom: 6, fontWeight: 600 }}>تعليق (اختياري)</label>
            <textarea value={rateComment} onChange={(e) => setRateComment(e.target.value)} rows={3} maxLength={1000} placeholder="اكتب رأيك في التعامل مع البائع..." style={{ width: '100%', padding: '11px 12px', border: '1.5px solid #E0E0E0', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, resize: 'none', marginBottom: 16, outline: 'none' }} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={submitReview} disabled={rateSubmitting || rateStars < 1} style={{ flex: 1, background: '#005343', color: '#fff', border: 'none', padding: '11px', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: (rateSubmitting || rateStars < 1) ? 0.6 : 1 }}>{rateSubmitting ? 'جاري الإرسال...' : 'إرسال التقييم'}</button>
              <button onClick={() => setRateOpen(false)} style={{ padding: '11px 20px', background: '#F5F5F5', color: '#616161', border: 'none', borderRadius: 10, fontFamily: "'Cairo', sans-serif", fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>إلغاء</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdDetailPage;
